import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { ChartComponent } from '@next/core-lib/chart';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import * as Highcharts from 'highcharts';

import { FeedAdviceLine, FeedAdviceType } from '../../models/feed-advice.model';

@Component({
  selector: 'app-feed-advice-graph',
  templateUrl: './feed-advice-graph.component.html',
  styleUrls: ['./feed-advice-graph.component.scss'],
  imports: [
    MatRadioGroup,
    ReactiveFormsModule,
    FormsModule,
    MatRadioButton,
    ChartComponent,
    TranslatePipe,
  ],
})
export class FeedAdviceGraphComponent implements OnInit {
  @Input() lines: FeedAdviceLine[];
  @Input() feedAdviceType: string;
  leftYaxis = 'weight';
  rightYaxis = 'feedintake';
  chartOptions: Highcharts.Options;
  chartSeries: Highcharts.SeriesOptionsType[];
  @ViewChild(ChartComponent) chartComponent: ChartComponent;

  constructor(@Inject(LocaleService) private readonly localeService: LocaleService) {}

  ngOnInit() {
    this.chartOptions = this.getInitialOptions();
    this.createChart();
  }

  createChart(): void {
    this.chartSeries = [
      {
        yAxis: 0,
        name: this.localeService.translate(`feed-advice-graph.${this.leftYaxis}`),
        type: 'line',
        data: this.getData(this.leftYaxis),
      },
      {
        yAxis: 1,
        name: this.localeService.translate(`feed-advice-graph.${this.rightYaxis}`),
        type: 'line',
        data: this.getData(this.rightYaxis),
      },
    ];
  }

  changeSetting() {
    this.chartComponent.options = <Highcharts.Options>{
      ...this.chartOptions,
      yAxis: [
        {
          title: this.localeService.translate(`feed-advice-graph.${this.leftYaxis}`),
        },
        {
          title: this.localeService.translate(`feed-advice-graph.${this.rightYaxis}`),
          opposite: true,
        },
      ],
    };
    // redrawing the graph
    this.chartComponent.chart.removeSeries(0);
    this.chartComponent.chart.removeSeries(0);
    this.chartComponent.chart.addSeries(
      {
        yAxis: 0,
        name: this.localeService.translate(`feed-advice-graph.${this.leftYaxis}`),
        type: 'line',
        data: this.getData(this.leftYaxis),
      },
      true,
      false,
    );
    this.chartComponent.chart.addSeries(
      {
        yAxis: 1,
        name: this.localeService.translate(`feed-advice-graph.${this.rightYaxis}`),
        type: 'line',
        data: this.getData(this.rightYaxis),
      },
      true,
      false,
    );
  }

  private isWeanedPiglets(): boolean {
    return this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS;
  }

  private getInitialOptions(): Highcharts.Options {
    const xaxisText = this.isWeanedPiglets() ? 'day' : 'week';
    return {
      chart: {
        height: 450,
        width: 720,
        zooming: {
          mouseWheel: false,
        },
      },
      xAxis: {
        allowDecimals: false,
        title: {
          enabled: true,
          text: this.localeService.translate(`feed-advice-report.table.${xaxisText}`),
        },
      },
      yAxis: [
        {
          title: this.localeService.translate(`feed-advice-graph.${this.leftYaxis}`),
        },
        {
          title: this.localeService.translate(`feed-advice-graph.${this.rightYaxis}`),
          opposite: true,
        },
      ],
      tooltip: {
        shared: true,
      },
      plotOptions: {
        line: {
          animation: false,
        },
      },
      credits: {
        enabled: false,
      },
    } as Highcharts.Options;
  }

  private getData(dataElement: string) {
    if (!this.lines) {
      return [];
    }
    switch (dataElement) {
      case 'weight':
        return this.lines.map((line) => [
          this.isWeanedPiglets() ? line.day : line.week,
          line.weight,
        ]);
      case 'energy':
        return this.lines.map((line) => [
          this.isWeanedPiglets() ? line.day : line.week,
          Math.round(line.energyValuePerDay * 1000) / 1000,
        ]);
      case 'lysine':
        return this.lines.map((line) => [
          this.isWeanedPiglets() ? line.day : line.week,
          Math.round(line.intestinalDigestibleLysinePerDay * 1000) / 1000,
        ]);
      case 'feedintake':
        return this.lines.map((line) => [
          this.isWeanedPiglets() ? line.day : line.week,
          Math.round(line.foodPerDay * 1000) / 1000,
        ]);
    }
  }
}
